import { getCurrentYear } from 'common-react';
import {
  ZUS_CONTRIBUTION_RATE,
  getLifeExpectancyMonths,
  getZusAccumulatedCapital,
} from '../data';
import {
  DEFAULT_AVERAGE_RETURN_RATE,
  DEFAULT_PAYOUT_YEARS,
  EmploymentType,
  FALLBACK_MAX_MONTHLY_INVESTMENT,
  Gender,
  MONTHS_IN_YEAR,
  PROMOTED_PRODUCT_SALARY_THRESHOLD,
  RETIREMENT_AGE,
  RetirementCalculatorField,
  SAVINGS_PRODUCTS_ORDER,
  SAVINGS_PRODUCT_RULES,
  SavingsField,
  SavingsProduct,
} from './RetirementCalculator.const';
import {
  RetirementCalculatorFormValuesModel,
  RetirementCalculatorSettingsModel,
  RetirementCalculatorSettingsOverridesModel,
  RetirementProjectionModel,
  SavingsPayoutBreakdownModel,
  SavingsProductValueModel,
  SavingsValuesModel,
  ZusPensionModel,
} from './RetirementCalculator.model';
import { getAgeFromBirthYear } from './RetirementCalculator.utils';

export const DEFAULT_RETIREMENT_CALCULATOR_SETTINGS: RetirementCalculatorSettingsModel = {
  retirementAge: RETIREMENT_AGE,
  payoutYears: DEFAULT_PAYOUT_YEARS,
  averageReturnRate: DEFAULT_AVERAGE_RETURN_RATE,
  yearlyContributionLimits: {},
};

export const resolveSettings = (
  settings?: Partial<RetirementCalculatorSettingsModel>,
): RetirementCalculatorSettingsModel => {
  const definedSettings = (Object.keys(settings ?? {}) as Array<
    keyof RetirementCalculatorSettingsModel
  >).reduce<Partial<RetirementCalculatorSettingsModel>>((result, key) => {
    if (settings?.[key] !== undefined) {
      Object.assign(result, { [key]: settings[key] });
    }
    return result;
  }, {});

  return {
    ...DEFAULT_RETIREMENT_CALCULATOR_SETTINGS,
    ...definedSettings,
    retirementAge: { ...RETIREMENT_AGE, ...settings?.retirementAge },
  };
};

export const applySettingsOverrides = (
  settings: RetirementCalculatorSettingsModel,
  overrides: RetirementCalculatorSettingsOverridesModel,
  gender?: Gender,
): RetirementCalculatorSettingsModel => ({
  ...settings,
  averageReturnRate: overrides.averageReturnRate ?? settings.averageReturnRate,
  payoutYears: overrides.payoutYears ?? settings.payoutYears,
  retirementAge:
    gender !== undefined && overrides.retirementAge !== undefined
      ? {
          ...settings.retirementAge,
          [gender]: overrides.retirementAge,
        }
      : settings.retirementAge,
});

export const getYearsToRetirement = (
  age: number,
  gender: Gender,
  settings: RetirementCalculatorSettingsModel,
) => Math.max(0, Math.ceil(settings.retirementAge[gender] - age));

export const getMonthsToRetirement = (
  age: number,
  gender: Gender,
  settings: RetirementCalculatorSettingsModel,
) => getYearsToRetirement(age, gender, settings) * MONTHS_IN_YEAR;

const getFutureValue = (
  presentValue: number,
  yearlyContribution: number,
  years: number,
  returnRate: number,
) => {
  if (returnRate === 0) {
    return presentValue + yearlyContribution * years;
  }

  const growth = (1 + returnRate) ** years;
  return presentValue * growth + yearlyContribution * ((growth - 1) / returnRate);
};

export const calculateProductCapital = (
  product: SavingsProduct,
  value: SavingsProductValueModel,
  yearsToRetirement: number,
  settings: RetirementCalculatorSettingsModel,
) => {
  const rules = SAVINGS_PRODUCT_RULES[product];
  const accumulated = value[SavingsField.ACCUMULATED] ?? 0;
  const monthlyContribution = value[SavingsField.MONTHLY_CONTRIBUTION] ?? 0;

  const yearlyLimit = settings.yearlyContributionLimits[product];
  const yearlyContribution =
    rules.hasYearlyLimit && yearlyLimit !== undefined
      ? Math.min(monthlyContribution * MONTHS_IN_YEAR, yearlyLimit)
      : monthlyContribution * MONTHS_IN_YEAR;

  return getFutureValue(
    accumulated,
    yearlyContribution,
    yearsToRetirement,
    rules.earnsReturn ? settings.averageReturnRate : 0,
  );
};

export const calculateProductMonthlyPayout = (
  product: SavingsProduct,
  value: SavingsProductValueModel,
  yearsToRetirement: number,
  settings: RetirementCalculatorSettingsModel,
) => {
  const payoutMonths = settings.payoutYears * MONTHS_IN_YEAR;
  if (payoutMonths <= 0) {
    return 0;
  }

  const capital = calculateProductCapital(product, value, yearsToRetirement, settings);
  return (capital / payoutMonths) * (1 - SAVINGS_PRODUCT_RULES[product].payoutTaxRate);
};

export const calculateSavingsPayoutBreakdown = (
  savings: SavingsValuesModel | undefined,
  basicData: RetirementCalculatorFormValuesModel,
  settings: RetirementCalculatorSettingsModel,
  currentYear = getCurrentYear(),
): SavingsPayoutBreakdownModel => {
  const birthYear = basicData[RetirementCalculatorField.BIRTH_YEAR];
  const gender = basicData[RetirementCalculatorField.GENDER];

  if (!savings || birthYear === undefined || gender === undefined) {
    return {};
  }

  const yearsToRetirement = getYearsToRetirement(
    getAgeFromBirthYear(birthYear, currentYear),
    gender,
    settings,
  );

  return SAVINGS_PRODUCTS_ORDER.reduce<SavingsPayoutBreakdownModel>((breakdown, product) => {
    if (savings[product]?.enabled) {
      breakdown[product] = calculateProductMonthlyPayout(
        product,
        savings[product],
        yearsToRetirement,
        settings,
      );
    }
    return breakdown;
  }, {});
};

/**
 * ZUS pension = accumulated capital divided by the average remaining life
 * expectancy at retirement age (the ZUS method).
 *
 * The capital is the sum of the account and subaccount balances from the ZUS
 * tables for a given birth year and gender, plus contributions paid until
 * retirement age. The calculation uses today's values without indexing
 * contributions or capital, making the replacement rate comparable to the
 * current salary.
 */
export const calculateZusPension = (
  values: RetirementCalculatorFormValuesModel,
  settings: RetirementCalculatorSettingsModel,
  currentYear = getCurrentYear(),
): ZusPensionModel | undefined => {
  const salary = values[RetirementCalculatorField.MONTHLY_GROSS_SALARY];
  const birthYear = values[RetirementCalculatorField.BIRTH_YEAR];
  const gender = values[RetirementCalculatorField.GENDER];

  if (salary === undefined || birthYear === undefined || gender === undefined) {
    return undefined;
  }

  const age = getAgeFromBirthYear(birthYear, currentYear);
  const monthsToRetirement = getMonthsToRetirement(age, gender, settings);

  const accumulatedCapital = getZusAccumulatedCapital(birthYear, gender);
  const futureContributions = salary * ZUS_CONTRIBUTION_RATE * monthsToRetirement;
  const totalCapital = accumulatedCapital + futureContributions;

  const payoutMonths = getLifeExpectancyMonths(settings.retirementAge[gender]);

  return {
    monthlyPension: payoutMonths > 0 ? totalCapital / payoutMonths : 0,
    accumulatedCapital,
    futureContributions,
    totalCapital,
    monthsToRetirement,
    payoutMonths,
  };
};

export const calculateProjection = (
  basicData: RetirementCalculatorFormValuesModel | undefined,
  savings: SavingsValuesModel | undefined,
  settings: RetirementCalculatorSettingsModel,
  currentYear = getCurrentYear(),
  promoted?: { product: PromotedProduct; monthlyInvestment: number },
): RetirementProjectionModel | undefined => {
  if (!basicData) {
    return undefined;
  }

  const zusPension = calculateZusPension(basicData, settings, currentYear);
  if (zusPension === undefined) {
    return undefined;
  }

  const { monthlyPension } = zusPension;

  const breakdown = calculateSavingsPayoutBreakdown(savings, basicData, settings, currentYear);
  const savingsMonthlyPayout = Object.values(breakdown).reduce<number>(
    (sum, payout) => sum + (payout ?? 0),
    0,
  );
  const salary = basicData[RetirementCalculatorField.MONTHLY_GROSS_SALARY];

  const promotedProductPayout =
    promoted && promoted.monthlyInvestment > 0
      ? calculatePromotedProductPayout(
          promoted.product,
          promoted.monthlyInvestment,
          basicData,
          settings,
          currentYear,
        )
      : 0;

  return {
    monthlyPension,
    zusPension,
    salaryReplacementRate: salary ? (monthlyPension / salary) * 100 : undefined,
    savingsMonthlyPayout,
    totalMonthlyPayout: monthlyPension + savingsMonthlyPayout,
    savingsBreakdown: breakdown,
    promotedProductPayout,
  };
};

export const getMaxMonthlyInvestment = (
  product: PromotedProduct,
  settings: RetirementCalculatorSettingsModel,
) => {
  const yearlyLimit = settings.yearlyContributionLimits[product];
  return yearlyLimit === undefined
    ? FALLBACK_MAX_MONTHLY_INVESTMENT
    : Math.floor(yearlyLimit / MONTHS_IN_YEAR);
};

export const calculatePromotedProductPayout = (
  product: PromotedProduct,
  monthlyInvestment: number,
  basicData: RetirementCalculatorFormValuesModel,
  settings: RetirementCalculatorSettingsModel,
  currentYear = getCurrentYear(),
) => {
  const birthYear = basicData[RetirementCalculatorField.BIRTH_YEAR];
  const gender = basicData[RetirementCalculatorField.GENDER];

  if (birthYear === undefined || gender === undefined) {
    return 0;
  }

  const yearsToRetirement = getYearsToRetirement(
    getAgeFromBirthYear(birthYear, currentYear),
    gender,
    settings,
  );

  return calculateProductMonthlyPayout(
    product,
    { enabled: true, [SavingsField.MONTHLY_CONTRIBUTION]: monthlyInvestment },
    yearsToRetirement,
    settings,
  );
};

export type PromotedProduct = SavingsProduct.IKE | SavingsProduct.IKZE;

export const getPromotedProduct = (
  savings: SavingsValuesModel | undefined,
  employmentType?: EmploymentType,
  monthlyGrossSalary?: number,
): PromotedProduct | undefined => {
  const hasIke = !!savings?.[SavingsProduct.IKE]?.enabled;
  const hasIkze = !!savings?.[SavingsProduct.IKZE]?.enabled;

  if (hasIke && hasIkze) {
    return undefined;
  }
  if (hasIke) {
    return SavingsProduct.IKZE;
  }
  if (hasIkze) {
    return SavingsProduct.IKE;
  }

  return employmentType === EmploymentType.BUSINESS ||
    (monthlyGrossSalary ?? 0) > PROMOTED_PRODUCT_SALARY_THRESHOLD
    ? SavingsProduct.IKZE
    : SavingsProduct.IKE;
};
